import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import { getUser } from '@/utils/auth';
import { Sparkles, Plus, AlertCircle, Layers, ArrowRight, Home, ChefHat, BedDouble, Sofa, BookOpen, DoorOpen, Armchair } from 'lucide-react';

// Import refactored components
import {
  VARIANTS,
  ROOM_TEMPLATE_TYPES,
  createDefaultRoom,
  createRoomFromTemplate,
  DEFAULT_BUDGET_CONFIG,
  DEFAULT_FORM_DATA,
  StyleSelector,
  RoomInput,
  ContactForm,
  QuoteResult,
  SmartEstimatorForm,
  WhyChooseSection,
  HeroSection,
  ConsultationModal
} from '@/components/interior-design';

const InteriorDesignNew = () => {
  const [searchParams] = useSearchParams();
  const user = getUser();
  
  // Form state
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [variant, setVariant] = useState('modern');
  
  // Room-by-room mode state - Start with Room 1 (general room with wardrobe)
  const [rooms, setRooms] = useState([createDefaultRoom('general', 1)]);
  const [roomCounter, setRoomCounter] = useState(1);
  
  // Smart estimator mode state
  const [budget, setBudget] = useState('');
  const [carpetArea, setCarpetArea] = useState('');
  const [budgetConfig, setBudgetConfig] = useState(DEFAULT_BUDGET_CONFIG);
  
  // UI state - Smart Generator as default
  const [estimateMode, setEstimateMode] = useState('smart');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [showConsultation, setShowConsultation] = useState(false);
  const [showRoomTemplateSelector, setShowRoomTemplateSelector] = useState(false);

  // Check for pending quote after login
  useEffect(() => {
    const action = searchParams.get('action');
    const pendingQuote = localStorage.getItem('pending_quote');
    const pendingConsultationQuote = localStorage.getItem('pending_consultation_quote');
    
    if (user && (pendingQuote || pendingConsultationQuote)) {
      const savedQuote = pendingConsultationQuote ? JSON.parse(pendingConsultationQuote) : JSON.parse(pendingQuote);
      
      if (savedQuote) {
        setQuote(savedQuote);
        setStep(2);
        
        if (action === 'save_quote') {
          // Auto-save the quote
          apiClient.post('/interior-design/quotes/save', { quote_id: savedQuote.quote_id })
            .then(() => {
              toast.success('Your quote has been saved to your profile!');
              localStorage.removeItem('pending_quote');
            })
            .catch(() => {
              toast.info('Quote restored. You can save it from the quote page.');
            });
        } else if (action === 'book_consultation' || pendingConsultationQuote) {
          toast.info('Quote restored! You can now proceed with booking your consultation.');
          localStorage.removeItem('pending_consultation_quote');
          localStorage.removeItem('pending_quote');
        }
      }
    }
  }, [user, searchParams]);

  // Add a room from template
  const addRoomFromTemplate = (templateType) => {
    if (templateType === 'kitchen') {
      const newRoom = createRoomFromTemplate('kitchen', 0, variant);
      setRooms([...rooms, newRoom]);
    } else {
      const newCounter = roomCounter + 1;
      const newRoom = createRoomFromTemplate(templateType, newCounter, variant);
      setRoomCounter(newCounter);
      setRooms([...rooms, newRoom]);
    }
    setShowRoomTemplateSelector(false);
  };

  const removeRoom = (index) => {
    if (rooms.length > 1) {
      setRooms(rooms.filter((_, i) => i !== index));
    }
  };

  const updateRoom = (index, field, value) => {
    const updated = [...rooms];
    
    // Support batch updates by passing an object
    if (typeof field === 'object' && field !== null) {
      updated[index] = { ...updated[index], ...field };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    
    // Auto-update defaults when room type changes
    if (field === 'room_type' || (typeof field === 'object' && field.room_type)) {
      const roomType = typeof field === 'object' ? field.room_type : value;
      if (roomType === 'kitchen') {
        updated[index].frontage_length = 12;
        updated[index].height = 9.5;
        updated[index].kitchen_package = 'basic';
        updated[index].sub_items = [];
      } else if (roomType === 'general') {
        // Add a default wardrobe when switching to general
        if (!updated[index].sub_items || updated[index].sub_items.length === 0) {
          updated[index].sub_items = [{
            item_type: 'wardrobe',
            item_name: 'Wardrobe',
            frontage_length: 8,
            height: 9.5,
            add_profile_lighting: false,
            add_mirror: false
          }];
        }
        updated[index].kitchen_package = null;
      }
    }
    
    setRooms(updated);
  };

  // Handle room-by-room quote submission
  const handleRoomQuote = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in your contact details');
      return;
    }

    if (rooms.length === 0) {
      toast.error('Please add at least one room');
      return;
    }

    setLoading(true);

    try {
      // Send rooms directly in new format - backend now supports sub-items natively
      const apiRooms = rooms.map(room => {
        if (room.room_type === 'kitchen') {
          return {
            room_type: 'kitchen',
            room_name: room.room_name || 'Kitchen',
            frontage_length: room.frontage_length,
            height: room.height || 9.5,
            kitchen_package: room.kitchen_package || 'basic',
            tall_uppers: room.tall_uppers || false,
            add_loft: room.add_loft || false,
            shutter_addons: room.shutter_addons || null,
            add_profile_lighting: room.add_profile_lighting || false,
            include_false_ceiling: room.include_false_ceiling || false,
            false_ceiling_sqft: room.false_ceiling_sqft || 0,
            add_ceiling_profile_lights: room.add_ceiling_profile_lights || false,
            sub_items: []  // Kitchen has no sub-items
          };
        } else {
          // General room with sub-items
          return {
            room_type: 'general',
            room_name: room.room_name || 'Room',
            frontage_length: room.frontage_length || 8,
            height: room.height || 9.5,
            include_false_ceiling: room.include_false_ceiling || false,
            false_ceiling_sqft: room.false_ceiling_sqft || 0,
            add_ceiling_profile_lights: room.add_ceiling_profile_lights || false,
            sub_items: (room.sub_items || []).map(item => ({
              item_type: item.item_type,
              item_name: item.item_name || item.item_type,
              frontage_length: item.frontage_length,
              height: item.height || 9.5,
              add_profile_lighting: item.add_profile_lighting || false,
              add_mirror: item.add_mirror || false,
              shutter_addons: item.shutter_addons || null
            }))
          };
        }
      });

      if (apiRooms.length === 0) {
        toast.error('Please add at least one room');
        setLoading(false);
        return;
      }

      const payload = {
        ...formData,
        variant,
        rooms: apiRooms
      };

      const response = await apiClient.post('/interior-design/quote', payload);
      setQuote(response.data);
      setStep(2);
      toast.success('Your quote is ready!');
    } catch (error) {
      console.error('Quote error:', error);
      toast.error('Failed to generate quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle smart estimator quote submission
  const handleSmartQuote = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in your contact details');
      return;
    }

    if (!budget || parseFloat(budget) < 300000) {
      toast.error('Please enter a budget of at least ₹3 Lakhs');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        property_type: formData.property_type,
        budget: parseFloat(budget),
        total_carpet_area: carpetArea ? parseFloat(carpetArea) : null,
        bedrooms: budgetConfig.bedrooms,
        include_living_room: budgetConfig.include_living_room,
        kitchen_layout: budgetConfig.kitchen_layout,
        kitchen_package: budgetConfig.kitchen_package || 'basic',
        preferred_variant: budgetConfig.preferred_variant || null,
        additional_notes: formData.additional_notes || null
      };

      const response = await apiClient.post('/interior-design/quote-by-budget', payload);
      setQuote(response.data);
      setStep(2);
      toast.success('Your personalized quote is ready!');
    } catch (error) {
      toast.error('Failed to generate quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Reset to start new quote
  const resetQuote = () => {
    setStep(1);
    setQuote(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <HeroSection />

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4">
          
          {/* Step 1: Quote Form */}
          {step === 1 && (
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              
              {/* Mode Toggle - Enhanced to stand out */}
              <div className="flex justify-center mb-8">
                <div className="inline-flex rounded-2xl bg-gradient-to-r from-primary/10 to-amber-100 p-1.5 shadow-lg border border-primary/20">
                  <button
                    type="button"
                    onClick={() => setEstimateMode('room')}
                    className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                      estimateMode === 'room'
                        ? 'bg-white text-primary shadow-md scale-105 border-2 border-primary'
                        : 'text-gray-600 hover:text-primary hover:bg-white/50'
                    }`}
                    data-testid="mode-room"
                  >
                    <Layers className="w-5 h-5 inline mr-2" />
                    Room-by-Room
                  </button>
                  <button
                    type="button"
                    onClick={() => setEstimateMode('smart')}
                    className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                      estimateMode === 'smart'
                        ? 'bg-white text-amber-600 shadow-md scale-105 border-2 border-amber-500'
                        : 'text-gray-600 hover:text-amber-600 hover:bg-white/50'
                    }`}
                    data-testid="mode-smart"
                  >
                    <Sparkles className="w-5 h-5 inline mr-2" />
                    Smart AI Estimator
                  </button>
                </div>
              </div>

              {/* Mode Description */}
              <div className="text-center mb-8">
                {estimateMode === 'room' ? (
                  <p className="text-gray-600">
                    Add each room individually for a detailed, precise quote.
                  </p>
                ) : (
                  <p className="text-gray-600">
                    Tell us your budget and preferences — we&apos;ll customize the perfect solution for you.
                  </p>
                )}
              </div>

              {/* Contact Form */}
              <ContactForm formData={formData} onFormChange={setFormData} />

              {/* Room-by-Room Mode */}
              {estimateMode === 'room' && (
                <form onSubmit={handleRoomQuote} className="space-y-6">
                  
                  {/* Style Selector */}
                  <StyleSelector selectedVariant={variant} onVariantChange={setVariant} />

                  {/* Rooms Section */}
                  <div className="border-t pt-6">
                    <h3 className="text-base font-bold mb-4">Room Details</h3>

                    {/* Room List */}
                    <div className="space-y-4">
                      {rooms.map((room, idx) => (
                        <RoomInput
                          key={room.id || `room-${idx}`}
                          room={room}
                          index={idx}
                          onUpdate={updateRoom}
                          onRemove={removeRoom}
                          canRemove={rooms.length > 1}
                          isCollapsed={idx < rooms.length - 1}
                          globalStyle={variant}
                        />
                      ))}
                    </div>
                    
                    {/* Add Room Template Selector */}
                    {showRoomTemplateSelector ? (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                        <h4 className="font-semibold text-sm mb-3">Select Room Type</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {ROOM_TEMPLATE_TYPES.map(template => {
                            const IconComp = template.icon;
                            return (
                              <button
                                key={template.value}
                                type="button"
                                onClick={() => addRoomFromTemplate(template.value)}
                                className="p-3 border rounded-lg hover:border-primary hover:bg-primary/5 text-left transition-all"
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <IconComp className="w-4 h-4 text-primary" />
                                  <span className="font-medium text-sm">{template.label}</span>
                                </div>
                                <p className="text-xs text-gray-500 line-clamp-2">{template.description}</p>
                              </button>
                            );
                          })}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowRoomTemplateSelector(false)}
                          className="mt-3"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-4">
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          onClick={() => setShowRoomTemplateSelector(true)}
                          className="gap-1"
                          data-testid="add-room-btn"
                        >
                          <Plus className="w-4 h-4" /> Add Room
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    className="w-full h-14 text-lg gap-2"
                    disabled={loading}
                    data-testid="generate-quote-btn"
                  >
                    {loading ? (
                      <>Generating Quote...</>
                    ) : (
                      <>
                        Generate My Quote <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* Smart AI Estimator Mode */}
              {estimateMode === 'smart' && (
                <form onSubmit={handleSmartQuote} className="space-y-6">
                  
                  <SmartEstimatorForm
                    budget={budget}
                    onBudgetChange={setBudget}
                    carpetArea={carpetArea}
                    onCarpetAreaChange={setCarpetArea}
                    budgetConfig={budgetConfig}
                    onConfigChange={setBudgetConfig}
                  />

                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    className="w-full h-14 text-lg gap-2"
                    disabled={loading}
                    data-testid="smart-quote-btn"
                  >
                    {loading ? (
                      <>Generating Smart Quote...</>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" /> Get Smart Estimate
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* Why Choose Section */}
              <WhyChooseSection />
            </div>
          )}

          {/* Step 2: Quote Result */}
          {step === 2 && quote && (
            <div className="space-y-6">
              <QuoteResult quote={quote} estimateMode={estimateMode} onReset={resetQuote} formData={formData} />

              {/* Info Banner */}
              <div className={`border rounded-lg p-4 text-center ${quote.email_sent !== false ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
                <AlertCircle className={`w-5 h-5 inline mr-2 ${quote.email_sent !== false ? 'text-blue-600' : 'text-amber-600'}`} />
                <span className={quote.email_sent !== false ? 'text-blue-800' : 'text-amber-800'}>
                  {quote.email_sent !== false
                    ? 'A copy of this quote has been sent to your email.'
                    : 'We could not send the quote to your email. You can download or screenshot this page.'}
                </span>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* Consultation Modal */}
      <ConsultationModal
        isOpen={showConsultation}
        onClose={() => setShowConsultation(false)}
        quoteId={quote?.quote_id}
        formData={formData}
      />
    </div>
  );
};

export default InteriorDesignNew;
